import { useState } from 'react';

import { useTranslate } from '@spazious/dollhouse-i18n';
import { useStore } from '@spazious/dollhouse-store';
import { UserSettings } from '@spazious/dollhouse-types';
import {
  Button,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  DialogActions,
  DialogActionsProps,
  Flex,
} from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

export interface ConfirmDialogActionsProps extends DialogActionsProps {
  setting?: keyof UserSettings;
  onConfirm?: (evt: {}) => void;
  onClose?: (
    evt: {},
    reason: 'backdropClick' | 'escapeKeyDown' | 'btnCancelClick' | 'btnConfirmClick'
  ) => void;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  showCloseButton?: boolean;
}

export const ConfirmDialogActions: React.FC<ConfirmDialogActionsProps> = ({
  setting,
  onConfirm,
  onClose,
  confirmButtonLabel = 'Confirm',
  cancelButtonLabel = 'Cancel',
  showCloseButton = false,
}) => {
  const translate = useTranslate();
  const set = useStore(({ set }) => set);
  const askCheckbox = useStore(({ settings }) => (setting ? settings[setting] : null));
  const [askCheckboxChecked, setAskCheckboxChecked] = useState(!askCheckbox);

  const handleConfirm = useEventCallback<PickProp<ButtonProps, 'onClick'>>((evt) => {
    onConfirm?.(evt);
    onClose?.(evt, 'btnConfirmClick');
    if (askCheckboxChecked && setting) {
      set(({ settings }) => ({
        settings: { ...settings, [setting]: false },
      }));
    }
  });

  const handleCancel = useEventCallback<PickProp<ButtonProps, 'onClick'>>((evt) => {
    onClose?.(evt, 'btnCancelClick');
  });

  const handleAskCheckboxChange = useEventCallback<PickProp<CheckboxProps, 'onChange'>>((evt) => {
    setAskCheckboxChecked(evt.target.checked);
  });

  return (
    <DialogActions
      style={{
        justifyContent: askCheckbox ? 'space-between' : 'flex-end',
      }}
    >
      {askCheckbox !== null && (
        <Checkbox
          checked={askCheckboxChecked}
          label={translate("Don't ask me again in the future")}
          onChange={handleAskCheckboxChange}
        />
      )}
      <Flex gap="var(--spacing-gap)">
        {showCloseButton && <Button onClick={handleCancel}>{cancelButtonLabel}</Button>}
        <Button color="primary" onClick={handleConfirm}>
          {confirmButtonLabel}
        </Button>
      </Flex>
    </DialogActions>
  );
};
